/**
 * LandlordQuoteModal — one-click "Send Quote for Approval" flow for jobs.
 * Mirrors the RequestSubQuotePanel UX pattern but sends TO the landlord
 * (owner) with a markup applied to the vendor's price. Landlord never sees
 * the vendor's raw price — only the total.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { X, Sparkle, Envelope, CurrencyDollar, FilePdf, PaperPlaneTilt, Camera } from "@phosphor-icons/react";
import { fileUrl } from "@/lib/api";

const MARKUP_CHIPS = [25, 30, 40, 50];

export default function LandlordQuoteModal({ job, onClose, onSaved }) {
  const existing = job?.landlord_quote || {};
  const [subs, setSubs] = useState([]);
  const [vendorId, setVendorId] = useState(existing.vendor_id || job?.assigned_sub_id || "");
  const [vendorName, setVendorName] = useState(existing.vendor_name || job?.assigned_sub_name || "");
  const [vendorCost, setVendorCost] = useState(
    existing.vendor_cost_cents ? (existing.vendor_cost_cents / 100).toFixed(2) : ""
  );
  const [markup, setMarkup] = useState(existing.markup_pct ?? 30);
  const [customMarkup, setCustomMarkup] = useState(
    ![25, 30, 40, 50].includes(existing.markup_pct) ? String(existing.markup_pct ?? "") : ""
  );
  const [scope, setScope] = useState(existing.scope || "");
  const [techNotes, setTechNotes] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [toEmail, setToEmail] = useState(existing.sent_to || "");
  const [toName, setToName] = useState(existing.sent_to_name || "");
  const [savedQuote, setSavedQuote] = useState(existing.token ? existing : null);
  // Photo picker — merges before/after/photos, dedupes by path
  const availablePhotos = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const p of [...(job?.before_photos || []), ...(job?.after_photos || []), ...(job?.photos || [])]) {
      const path = p?.path || p;
      if (!path || seen.has(path)) continue;
      seen.add(path);
      merged.push({ path, kind: p?.kind || (job?.before_photos?.some(x => x.path === path) ? "before" : (job?.after_photos?.some(x => x.path === path) ? "after" : "photo")) });
    }
    return merged;
  }, [job]);
  const [selectedPhotos, setSelectedPhotos] = useState(existing.photo_urls || []);
  const togglePhoto = (path) => {
    setSelectedPhotos((cur) => {
      if (cur.includes(path)) return cur.filter((p) => p !== path);
      if (cur.length >= 6) { toast.warning("Max 6 photos per quote"); return cur; }
      return [...cur, path];
    });
  };

  useEffect(() => {
    api.get("/sub-contractors").then((r) => setSubs(r.data || [])).catch(() => {});
  }, []);

  const vendorCostCents = Math.max(0, Math.round((parseFloat(vendorCost) || 0) * 100));
  const markupPct = Number(markup) || 0;
  const markupCents = Math.round(vendorCostCents * (markupPct / 100));
  const landlordTotalCents = vendorCostCents + markupCents;

  const fmt = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const pickMarkup = (pct) => { setMarkup(pct); setCustomMarkup(""); };
  const onCustomMarkup = (v) => {
    setCustomMarkup(v);
    const n = parseFloat(v);
    if (!isNaN(n) && n >= 0 && n <= 200) setMarkup(n);
  };

  const draftScope = async () => {
    setDrafting(true);
    const tId = toast.loading("Claude drafting scope for landlord…");
    try {
      const { data } = await api.post(`/jobs/${job.id}/landlord-quote/draft-scope`, {
        vendor_name: vendorName, tech_notes: techNotes,
      });
      setScope(data?.scope || "");
      toast.success(data?.used_ai ? "Scope drafted — edit before saving" : "Draft ready (no AI key)", { id: tId });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Draft failed", { id: tId });
    } finally { setDrafting(false); }
  };

  const save = async () => {
    if (vendorCostCents <= 0) return toast.error("Enter the vendor's cost first");
    if (!scope.trim()) return toast.error("Draft or type a scope of work first");
    setSaving(true);
    const tId = toast.loading("Saving quote…");
    try {
      const { data } = await api.put(`/jobs/${job.id}/landlord-quote`, {
        vendor_id: vendorId, vendor_name: vendorName,
        vendor_cost_cents: vendorCostCents,
        markup_pct: markupPct,
        scope, photo_urls: selectedPhotos,
      });
      setSavedQuote(data.quote);
      toast.success("Quote saved — preview or send below", { id: tId });
      onSaved && onSaved();

      // Auto-trigger: if the quote crosses the tenant's owner-approval
      // threshold, prompt the PM to fire off the Approve/Reject email now
      // so nobody forgets after clicking "Save".
      if (data.owner_approval_recommended) {
        const totalDollars = ((data.quote?.landlord_total_cents || 0) / 100).toLocaleString();
        setTimeout(() => {
          if (window.confirm(
            `This quote ($${totalDollars}) crosses your owner-approval threshold.\n\n` +
            `Request the landlord's Approve/Reject decision now?`
          )) {
            // Bubble up an event the JobDetail card can listen for
            window.dispatchEvent(new CustomEvent("field-radar:auto-owner-approval", {
              detail: { jobId: job.id, estimateCents: data.quote?.landlord_total_cents || 0 },
            }));
            onClose && onClose();
          }
        }, 400);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed", { id: tId });
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!savedQuote?.token) return toast.error("Save the quote first");
    setSending(true);
    const tId = toast.loading("Emailing landlord…");
    try {
      const { data } = await api.post(`/jobs/${job.id}/landlord-quote/send`, {
        to_email: toEmail, to_name: toName, cc_admin: true,
      });
      toast.success(`Sent to ${data.to} · they'll get a link to sign`, { id: tId });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed", { id: tId });
    } finally { setSending(false); }
  };

  const authQ = () => encodeURIComponent(localStorage.getItem("access_token") || "");
  const previewUrl = savedQuote?.token
    ? `${API}/jobs/${job.id}/landlord-quote/pdf?auth=${authQ()}`
    : null;

  const sentStatus = savedQuote?.status;

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
         data-testid="landlord-quote-modal">
      <div className="bg-white w-full max-w-3xl border-4 border-[#0f172a] shadow-[8px_8px_0_#0f172a] my-8"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-[#0f172a] text-white p-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[.2em] text-yellow-300">
              Repair Quote · Send to Landlord
            </div>
            <h2 className="text-2xl font-black mt-1 truncate">
              {job.property_address}{job.unit ? ` · ${job.unit}` : ""}
            </h2>
          </div>
          <button data-testid="lq-close" onClick={onClose} className="p-2 hover:bg-white/10">
            <X weight="bold" size={22}/>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Vendor + cost */}
          <div className="rugged-card p-4 bg-slate-50">
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-2">
              Step 1 · Vendor &amp; Cost
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_180px] gap-2">
              <select value={vendorId}
                      onChange={(e) => {
                        setVendorId(e.target.value);
                        const s = subs.find((x) => x.id === e.target.value);
                        if (s) setVendorName(s.name);
                      }}
                      data-testid="lq-vendor-select"
                      className="h-11 px-2 border-2 border-[#0f172a] bg-white font-bold min-w-0 w-full truncate">
                <option value="">— Pick vendor (or type below) —</option>
                {subs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.company ? ` (${s.company})` : ""}{s.trade ? ` — ${s.trade}` : ""}
                  </option>
                ))}
              </select>
              <div className="relative">
                <CurrencyDollar weight="bold" size={16}
                                className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input value={vendorCost} onChange={(e) => setVendorCost(e.target.value)}
                       type="number" step="0.01" placeholder="Vendor cost"
                       data-testid="lq-vendor-cost"
                       className="w-full h-11 pl-7 pr-2 border-2 border-yellow-500 bg-yellow-50 font-bold"/>
                <div className="absolute -top-2 right-2 px-1 bg-white text-[9px] font-black uppercase tracking-widest text-yellow-700">Vendor price</div>
              </div>
            </div>
            {!vendorId && (
              <input value={vendorName} onChange={(e) => setVendorName(e.target.value)}
                     placeholder="Vendor name (e.g., LCV General Contractor)"
                     data-testid="lq-vendor-name"
                     className="mt-2 w-full h-10 px-3 border-2 border-[#0f172a] font-medium"/>
            )}
          </div>

          {/* Markup */}
          <div className="rugged-card p-4">
            <div className="text-xs font-black uppercase tracking-widest text-slate-600 mb-2">
              Step 2 · Markup
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {MARKUP_CHIPS.map((p) => (
                <button key={p} onClick={() => pickMarkup(p)}
                        data-testid={`lq-markup-${p}`}
                        className={`h-11 px-4 font-black uppercase text-xs tracking-widest border-2 transition-all
                          ${Number(markup) === p && !customMarkup
                            ? "bg-[#0f172a] text-white border-[#0f172a] shadow-[3px_3px_0_#00C4CC]"
                            : "bg-white text-[#0f172a] border-[#0f172a] hover:bg-slate-50"}`}>
                  {p}%
                </button>
              ))}
              <div className="inline-flex items-center border-2 border-[#0f172a] h-11 px-2 bg-white">
                <input value={customMarkup} onChange={(e) => onCustomMarkup(e.target.value)}
                       type="number" step="0.5" placeholder="custom"
                       data-testid="lq-markup-custom"
                       className="w-20 h-full text-center font-black text-sm outline-none"/>
                <span className="text-xs font-black text-slate-400">%</span>
              </div>
            </div>
            {/* Live totals */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="p-3 bg-slate-50 border-2 border-slate-200">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Vendor</div>
                <div className="text-lg font-black text-slate-800" data-testid="lq-summary-vendor">
                  {fmt(vendorCostCents)}
                </div>
              </div>
              <div className="p-3 bg-cyan-50 border-2 border-[#00C4CC]">
                <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">
                  Your margin ({markupPct}%)
                </div>
                <div className="text-lg font-black text-cyan-900" data-testid="lq-summary-margin">
                  {fmt(markupCents)}
                </div>
              </div>
              <div className="p-3 bg-yellow-100 border-2 border-yellow-500">
                <div className="text-[10px] font-black uppercase tracking-widest text-yellow-800">
                  Landlord pays
                </div>
                <div className="text-lg font-black text-yellow-900" data-testid="lq-summary-total">
                  {fmt(landlordTotalCents)}
                </div>
              </div>
            </div>
          </div>

          {/* Scope */}
          <div className="rugged-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-black uppercase tracking-widest text-slate-600">
                Step 3 · Scope of Work (for landlord)
              </div>
              <button onClick={draftScope} disabled={drafting}
                      data-testid="lq-ai-draft"
                      className="h-9 px-3 bg-[#0f172a] text-white font-black uppercase text-[11px] tracking-widest
                                 inline-flex items-center gap-1 hover:bg-slate-800 disabled:opacity-60">
                <Sparkle weight="fill" size={13}
                         className={drafting ? "animate-pulse text-yellow-300" : "text-[#00C4CC]"}/>
                {drafting ? "Drafting…" : "AI Draft Scope"}
              </button>
            </div>
            <textarea value={techNotes} onChange={(e) => setTechNotes(e.target.value)}
                      placeholder="(Optional) Tech notes to feed Claude — LCV said the trap arm is broken, needs full disposal replacement…"
                      rows={2} data-testid="lq-tech-notes"
                      className="w-full mb-2 p-2 border-2 border-slate-300 text-xs font-medium bg-slate-50"/>
            <textarea value={scope} onChange={(e) => setScope(e.target.value)}
                      placeholder="Click 'AI Draft Scope' above to auto-fill from job data + tech notes, then edit."
                      rows={9} data-testid="lq-scope"
                      className="w-full p-3 border-2 border-[#0f172a] text-sm font-medium bg-white
                                 focus:outline-none focus:ring-4 focus:ring-yellow-500/30"/>
          </div>

          {/* Photo picker */}
          {availablePhotos.length > 0 && (
            <div className="rugged-card p-4" data-testid="lq-photo-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-black uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
                  <Camera weight="fill" size={14}/> Step 3.5 · Attach Photos ({selectedPhotos.length}/6)
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  {availablePhotos.length} on job
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
                Landlords approve ~3× faster when they can see the damage. Pick up to 6.
              </p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {availablePhotos.slice(0, 24).map((p) => {
                  const on = selectedPhotos.includes(p.path);
                  return (
                    <button key={p.path} type="button"
                            onClick={() => togglePhoto(p.path)}
                            data-testid={`lq-photo-toggle-${p.path.slice(-12)}`}
                            className={`relative aspect-square border-2 overflow-hidden group
                              ${on ? "border-yellow-500 shadow-[3px_3px_0_#0f172a]" : "border-slate-300 hover:border-[#0f172a]"}`}>
                      <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover"/>
                      {on && (
                        <div className="absolute inset-0 bg-yellow-500/40 flex items-center justify-center">
                          <span className="h-6 w-6 rounded-full bg-yellow-500 text-[#0f172a] font-black text-sm flex items-center justify-center border-2 border-[#0f172a]">
                            {selectedPhotos.indexOf(p.path) + 1}
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] font-black uppercase tracking-widest px-1">
                        {p.kind}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sent status banner */}
          {sentStatus && sentStatus !== "draft" && (
            <div className="p-3 bg-emerald-50 border-2 border-emerald-500">
              <div className="text-xs font-black uppercase tracking-widest text-emerald-800">
                Status: {sentStatus}
              </div>
              {savedQuote?.signed_at && (
                <div className="text-xs text-emerald-900 mt-1">
                  Signed by <b>{savedQuote.signed_by_name}</b> at {savedQuote.signed_at}
                </div>
              )}
            </div>
          )}

          {/* Send controls */}
          {savedQuote?.token && (
            <div className="rugged-card p-4 bg-yellow-50 border-yellow-500">
              <div className="text-xs font-black uppercase tracking-widest text-yellow-800 mb-2">
                Step 4 · Send to Landlord
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                       placeholder="Landlord email (auto-filled from property)"
                       data-testid="lq-to-email"
                       className="h-11 px-3 border-2 border-[#0f172a] font-medium bg-white"/>
                <input value={toName} onChange={(e) => setToName(e.target.value)}
                       placeholder="Landlord name"
                       data-testid="lq-to-name"
                       className="h-11 px-3 border-2 border-[#0f172a] font-medium bg-white"/>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {previewUrl && (
                  <a href={previewUrl} target="_blank" rel="noreferrer"
                     data-testid="lq-preview-pdf"
                     className="h-11 px-4 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-widest
                                inline-flex items-center gap-2 hover:bg-slate-50">
                    <FilePdf weight="fill" size={16}/> Preview PDF
                  </a>
                )}
                <button onClick={send} disabled={sending}
                        data-testid="lq-send"
                        className="flex-1 h-11 px-4 bg-yellow-500 hover:bg-yellow-600 text-[#0f172a] font-black uppercase text-xs tracking-widest
                                   inline-flex items-center justify-center gap-2 disabled:opacity-60
                                   shadow-[3px_3px_0_#0f172a]">
                  <PaperPlaneTilt weight="fill" size={16}/>
                  {sending ? "Sending…" : (sentStatus && sentStatus !== "draft" ? "Send Again" : "Send Quote for Approval")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} data-testid="lq-cancel"
                  className="h-10 px-4 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-widest">
            Close
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="lq-save"
                  className="h-10 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-widest
                             inline-flex items-center gap-2 hover:bg-[#00a8af] disabled:opacity-60
                             shadow-[3px_3px_0_#0f172a]">
            <Envelope weight="fill" size={14}/>
            {saving ? "Saving…" : (savedQuote?.token ? "Update Draft" : "Save Draft")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
